import { useState } from "react";

import {
  Button,
  Box,
  Text,
  HoverCard,
  ComboboxProps,
  Center,
  useCombobox,
  Combobox,
  ComboboxChevron,
  TextInput,
  Stack,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconSearch, IconX } from "@tabler/icons-react";
import _ from "lodash";

import { getFamilyLabel } from "constants/mercer-definitions";
import { useMercerSearchRouteInfo } from "hooks/routing";
import { FamilyData, PositionData, SubFamilyData } from "services/mercer/MercerDataService";
import { useIsSmallerThan } from "ui/theme/helpers";
import { getNumberOfBars } from "utils/mercer";

import { SignalBar } from "./SignalBars";
import { exampleJobTitlesBySubFamilyCode } from "../../constants/comptryx-definitions";
import { PageContentBoxWithInternalPadding } from "../layouts/PageContentBox";
interface SearchByBarProps {
  families: FamilyData[];
  subFamilies: SubFamilyData[];
  positions: PositionData[];
}

interface ItemProps extends React.ComponentPropsWithoutRef<"div"> {
  option: {
    type: string;
    description?: string;
    label: string;
    data_point_count: number;
    value: string;
    children?: any[];
    parent?: any;
    disabled?: boolean;
    actDisabled?: boolean;
    level?: number;
  };
  checked: boolean;
  showLabelWithDescription?: boolean;
}

export const FilterBySearchString = ({ options, search }: { options: any[]; search: string }) => {
  return options.filter((option) => {
    if (option.searchString) {
      return option.searchString?.toLowerCase().includes(search.toLowerCase());
    } else {
      return option.label?.toLowerCase().includes(search.toLowerCase());
    }
  });
};

// eslint-disable-next-line react/display-name
export const SelectItem = ({ option, checked, showLabelWithDescription = false }: ItemProps) => {
  const {
    label,
    type,
    children,
    parent,
    data_point_count,
    description,
    disabled,
    actDisabled,
    level,
    ...others
  } = option;

  const disabledButtonTextColor = "#868E96";
  const enabledButtonTextColor = "#343A40";
  const buttonTextColor = actDisabled ? disabledButtonTextColor : enabledButtonTextColor;

  const defaultLevel = level ?? 1;

  const buttonPadding = parent ? defaultLevel * 12 : 12;
  const buttonFontWeight = parent ? 400 : 500;

  const SelectGroup = (
    <Button
      justify="space-between"
      p={18}
      pl={buttonPadding}
      pr={24}
      disabled={disabled}
      fullWidth
      size="sm"
      sx={{ cursor: actDisabled ? "not-allowed" : "pointer" }}
      styles={{
        label: {
          // this shouldnt be there but the button changes are flexing things weirdly
          // this probably shouldnt be a button at all
          minHeight: "100px",
        },
      }}
      rightSection={
        data_point_count !== undefined ? (
          <Box mr={-12}>
            <SignalBar numberOfBars={getNumberOfBars(data_point_count)} />
          </Box>
        ) : null
      }
      variant="subtle"
      bg={checked ? "gray.2" : undefined}
    >
      <Box
        maw={500}
        sx={{
          fontWeight: buttonFontWeight,
          color: buttonTextColor,
          fontSize: parent ? 14 : 15,
        }}
      >
        {label}
      </Box>
    </Button>
  );

  return description ? (
    <HoverCard openDelay={200} withinPortal width={360} shadow="md" position="right" offset={24}>
      <HoverCard.Target>{SelectGroup}</HoverCard.Target>
      <HoverCard.Dropdown>
        <Box p={16}>
          <Stack gap={4}>
            {showLabelWithDescription && (
              <Text size="xs" fw={400}>
                {label}
              </Text>
            )}
            <Text size="sm" fw={400}>
              {description}
            </Text>
          </Stack>
        </Box>
      </HoverCard.Dropdown>
    </HoverCard>
  ) : (
    SelectGroup
  );
};

export const SharedComboboxProps: ComboboxProps = {
  width: 600,
  shadow: "xl",
  dropdownPadding: 2,
};

export const MercerComboBox = (
  props: ComboboxProps & {
    data: { value: string; label: string; data_point_count: number; type: string }[];
  } & {
    onChange: (value: string) => void;
    value: string;
    placeholder?: string;
    maxDropdownHeight: number;
    w?: number;
    pr?: number;
    comboboxProps?: ComboboxProps;
    disabled?: boolean;
  },
) => {
  const combobox = useCombobox();
  const value = props.value;
  const relevantLabel = props.data.find((option) => option.value === value)?.label;
  const [search, setSearch] = useState(null);
  const [interimValue, setInterimValue] = useState(null);
  const [shouldShowDisabledOptions, setShouldShowDisabledOptions] = useState(false);

  const reformattedData = props.data
    .map((option) => {
      return {
        ...option,
        disabled: false,
        actDisabled: !option.data_point_count,
      };
    })
    .filter((option) => option.value !== "null" && option.label !== "null");

  const disabledOptionsData = reformattedData.filter(
    (option) => !option.data_point_count && option.type !== "family",
  );

  const relevantOptionsData = shouldShowDisabledOptions
    ? reformattedData
    : reformattedData.filter(
        // @ts-expect-error TS2339
        (option) => option.data_point_count || _.some(option.children, "data_point_count"),
      );

  const filteredOptionData = search
    ? FilterBySearchString({ options: relevantOptionsData, search: search })
    : relevantOptionsData;

  const options = filteredOptionData.map((option) => (
    <Combobox.Option key={option.value} value={option.value} m={0} p={0}>
      <SelectItem option={option} checked={option.value === value} />
    </Combobox.Option>
  ));

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={(val) => {
        const relevantOptionData = reformattedData.find((option) => option.value === val);
        if (relevantOptionData?.actDisabled) {
          return;
        }

        const interimLabel =
          props.data.find((option) => option.value === val)?.label ?? "Loading...";
        props.onChange(val);
        setSearch(null);
        // @ts-expect-error TS2345
        setInterimValue(interimLabel);
        combobox.closeDropdown();
      }}
    >
      <Combobox.Target>
        <TextInput
          disabled={props.disabled}
          w={props.w}
          pr={props.pr}
          rightSection={
            // @ts-expect-error TS2339
            props.value && props.clearable ? (
              <IconX
                cursor="pointer"
                size={16}
                onClick={() => {
                  setSearch(null);
                  setInterimValue(null);
                  combobox.closeDropdown();
                  // @ts-expect-error TS2345
                  props.onChange(null);
                }}
              />
            ) : (
              <ComboboxChevron />
            )
          }
          // @ts-expect-error TS2339
          rightSectionPointerEvents={props.value && props.clearable ? "all" : "none"}
          onClick={() => {
            if (combobox.dropdownOpened) {
              combobox.closeDropdown();
            } else {
              combobox.openDropdown();
              // @ts-expect-error TS2551
              combobox.targetRef?.current?.select();
            }
          }}
          placeholder={props.placeholder}
          value={interimValue ?? search ?? relevantLabel}
          onChange={(e) => {
            setInterimValue(null);
            // @ts-expect-error TS2345
            setSearch(e.currentTarget.value);
          }}
        ></TextInput>
      </Combobox.Target>
      <Combobox.Dropdown>
        {/*
         // @ts-expect-error TS2322 */}
        <Box w={props.comboboxProps?.width} bg="white">
          <Combobox.Options
            mah={props.maxDropdownHeight}
            style={{ overflowY: "auto" }}
            // @ts-expect-error TS2322
            w={props.comboboxProps?.width}
            bg="white"
          >
            {options}
          </Combobox.Options>
          {disabledOptionsData.length > 0 && (
            <Button
              p={8}
              mt={6}
              mb={2}
              sx={{ cursor: "pointer" }}
              onClick={() => setShouldShowDisabledOptions(!shouldShowDisabledOptions)}
              fullWidth
              size="xs"
              justify="left"
              variant="subtle"
            >
              {shouldShowDisabledOptions ? "‹ Hide " : "› Show "}
              options with no matching data points ({disabledOptionsData.length})
            </Button>
          )}
        </Box>
      </Combobox.Dropdown>
    </Combobox>
  );
};

export function SearchByBar({ subFamilies, families, positions }: SearchByBarProps): JSX.Element {
  const isSmall = useIsSmallerThan("lg");
  const { query, replaceQuery } = useMercerSearchRouteInfo();

  const dataPointList = [...subFamilies, ...families];

  const [selectedFamily, setSelectedFamily] = useState<string | null>(query.jobFamily ?? null);
  const [selectedSubFamily, setSelectedSubFamily] = useState<string | null>(
    query.subFamily ?? null,
  );

  const [opened, { toggle }] = useDisclosure(false);

  function canExecuteSearch(): boolean | undefined {
    return true;
  }

  function submit() {
    replaceQuery({
      jobFamily: [selectedFamily ?? ""],
      subFamily: [selectedSubFamily ?? ""],
      specialization: [],
      title: [],
      yoe: [],
    });
  }

  const subFamilySearchOptions = subFamilies.map((row) => {
    const all_position_typical_titles = positions
      .filter((position) => position.position_code.includes(row.sub_family_code))
      .map((position) => position.position_typical_titles)
      .join(" ");

    const sampleJobTitles = exampleJobTitlesBySubFamilyCode[row.sub_family_code]?.join(" ") ?? "";

    return {
      value: row.sub_family_code,
      label: row.sub_family_label,
      data_point_count: row.data_point_count,
      disabled: !row.data_point_count,
      type: "subfamily",
      parent: row.sub_family_code.split(".")[0],
      parentLabel: getFamilyLabel(row.sub_family_code.split(".")[0]),
      description: row.sub_family_description_summary || "No description currently available",
      searchString: `${row.sub_family_code.split(".")[0]} ${getFamilyLabel(row.sub_family_code.split(".")[0])}
      ${row.sub_family_code}
      ${row.sub_family_label}
      ${row.sub_family_description}
      ${row.sub_family_description_summary}
      ${all_position_typical_titles}
      ${sampleJobTitles}
      `,
    };
  });

  const familyList = families.map((row) => row.family_code);

  const combinedSearchOptions = [
    ...familyList.map((family) => {
      return {
        value: family,
        label: getFamilyLabel(family),
        type: "family",
        disabled: true,
        description:
          families.find((row) => row.family_code === family)?.family_description_summary ||
          "No description currently available",
        children: subFamilySearchOptions.filter((row) => row.parent === family),
        searchString: `${family} ${getFamilyLabel(family)} ${subFamilySearchOptions
          .filter((row) => row.parent === family)
          .map((row) => row.label)
          .join(" ")}
          ${families.find((row) => row.family_code === family)?.family_description}
          ${families.find((row) => row.family_code === family)?.family_description_summary}
          ${subFamilySearchOptions
            .filter((row) => row.parent === family)
            .map((row) => row.searchString)
            .join(" ")}
          `,
      };
    }),
    ...subFamilySearchOptions,
  ];

  const sortedHierarchicalCombinedSearchOptions = _.sortBy(combinedSearchOptions, (option) => {
    if (option.type === "family") {
      return option.label;
    } else {
      // @ts-expect-error TS2339
      return `${option.parentLabel} ${option.label}`;
    }
  });

  const renderCombinedSearch = () => {
    return (
      <MercerComboBox
        // @ts-expect-error TS2322
        data={sortedHierarchicalCombinedSearchOptions}
        placeholder="Search by Job Family"
        filter={FilterBySearchString}
        maxDropdownHeight={600}
        // @ts-expect-error TS2322
        value={selectedSubFamily ? `${selectedFamily}.${selectedSubFamily}` : selectedFamily}
        w={600}
        pr={8}
        onChange={(v) => {
          const typeOfSelectedOption = combinedSearchOptions.find(
            (option) => option.value === v,
          )?.type;
          if (typeOfSelectedOption === "family") {
            setSelectedFamily(v);
            setSelectedSubFamily(null);
          } else if (typeOfSelectedOption === "subfamily") {
            // @ts-expect-error TS2339
            const parent = combinedSearchOptions.find((option) => option.value === v)?.parent;
            setSelectedFamily(parent);
            setSelectedSubFamily(v.split(".")[1]);
          }
        }}
      />
    );
  };

  return (
    <>
      <PageContentBoxWithInternalPadding py={{ base: 4, lg: "inherit" }}>
        <Center py={20}>
          {renderCombinedSearch()}
          <Button
            size="sm"
            leftSection={<IconSearch size={16} />}
            onClick={submit}
            disabled={!canExecuteSearch()}
            data-heap-id="benchmarking-search-button"
            variant="primary"
          >
            Search
          </Button>
        </Center>
      </PageContentBoxWithInternalPadding>
    </>
  );
}
